import { useMemo, useState } from "react";
import { User, ChevronRight, Bell, BellOff, Settings2, Star, TrendingUp, Target, Crown, BarChart3, Lock } from "lucide-react";
import { useUserFavorites, PLAYER_LIMITS, type FavoritePlayer } from "@/hooks/useUserFavorites";
import { useAllTodaysGames, type TransformedGame } from "@/hooks/useLiveGames";
import { useFavoritePlayerStats, type LivePlayerStats } from "@/hooks/useFavoritePlayerStats";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Skeleton } from "@/components/ui/skeleton";
import FavoritePlayersModal from "./FavoritePlayersModal";
import PremiumPlayerStatsModal from "./PremiumPlayerStatsModal";
import SubscriptionModal from "./SubscriptionModal";
import AuthModal from "./AuthModal";

// Flag mapping for nationalities
const NATIONALITY_FLAGS: Record<string, string> = {
  USA: "🇺🇸", CAN: "🇨🇦", SWE: "🇸🇪", FIN: "🇫🇮", RUS: "🇷🇺",
  CZE: "🇨🇿", GER: "🇩🇪", SUI: "🇨🇭", SVK: "🇸🇰", LAT: "🇱🇻",
  AUT: "🇦🇹", DEN: "🇩🇰", NOR: "🇳🇴", FRA: "🇫🇷", GBR: "🇬🇧",
};

interface PlayerCardProps {
  player: FavoritePlayer;
  upcomingGame?: TransformedGame;
  stats?: LivePlayerStats | null;
  isLoading?: boolean;
  onViewDetails: () => void;
  hasPremiumAccess: boolean;
  translations: { viewFullStats: string; premiumStats: string; signInStats: string };
}

const PlayerCard = ({ player, upcomingGame, stats, isLoading, onViewDetails, hasPremiumAccess, translations }: PlayerCardProps) => {
  const flag = player.nationality ? NATIONALITY_FLAGS[player.nationality] || "" : "";
  const isGoalie = player.position === "G" || stats?.isGoalie;
  
  return (
    <div className="bg-secondary/50 rounded-xl p-4 border border-border hover:border-primary/30 transition-all group relative">
      <div className="flex items-start gap-3">
        {player.photo ? (
          <img
            src={player.photo}
            alt={player.name}
            className="w-12 h-12 rounded-full object-cover bg-secondary ring-2 ring-border"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-sm font-bold text-muted-foreground ring-2 ring-border">
            {player.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {flag && <span className="text-sm">{flag}</span>}
            <h3 className="font-medium text-foreground truncate text-sm">{player.name}</h3>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium text-[10px]">
              {player.position}
            </span>
            <span>{player.team}</span>
            <span className="text-muted-foreground/50">·</span>
            <span>{player.league}</span>
          </div>
        </div>
      </div>
      
      {/* Season Stats */}
      {stats && (
        <div className="mt-3 pt-3 border-t border-border/50">
          {isGoalie ? (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-display font-bold text-foreground">{stats.wins || 0}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Wins</p>
              </div>
              <div>
                <p className="text-lg font-display font-bold text-foreground">{stats.gaa?.toFixed(2) || "0.00"}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">GAA</p>
              </div>
              <div>
                <p className="text-lg font-display font-bold text-foreground">{stats.svPct ? (stats.svPct * 100).toFixed(1) + "%" : "0%"}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">SV%</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-1 text-center">
              <div>
                <p className="text-lg font-display font-bold text-foreground">{stats.goals}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">G</p>
              </div>
              <div>
                <p className="text-lg font-display font-bold text-foreground">{stats.assists}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">A</p>
              </div>
              <div>
                <p className="text-lg font-display font-bold text-primary">{stats.points}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">PTS</p>
              </div>
              <div>
                <p className={`text-lg font-display font-bold ${(stats.plusMinus || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {(stats.plusMinus || 0) >= 0 ? '+' : ''}{stats.plusMinus || 0}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">+/-</p>
              </div>
            </div>
          )}
          <div className="mt-2 flex items-center justify-center gap-1 text-[10px] text-muted-foreground">
            <Target className="w-3 h-3" />
            <span>{stats.gp} GP</span>
          </div>
        </div>
      )}
      
      {!stats && isLoading && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <div className="grid grid-cols-4 gap-1">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <Skeleton className="h-5 w-8" />
                <Skeleton className="h-2 w-4" />
              </div>
            ))}
          </div>
        </div>
      )}
      
      {!stats && !isLoading && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-2">
            <TrendingUp className="w-3 h-3" />
            <span>{translations.signInStats}</span>
          </div>
        </div>
      )}
      
      {upcomingGame && (
        <div className="mt-2 pt-2 border-t border-border/30">
          <div className="flex items-center gap-2 text-xs">
            {upcomingGame.isLive ? (
              <>
                <span className="w-2 h-2 bg-score-live rounded-full animate-pulse" />
                <span className="text-score-live font-semibold">LIVE</span>
                <span className="text-muted-foreground">
                  {upcomingGame.homeAbbr} {upcomingGame.homeScore} - {upcomingGame.awayScore} {upcomingGame.awayAbbr}
                </span>
              </>
            ) : (
              <>
                <span className="text-primary font-medium">Next:</span>
                <span className="text-muted-foreground">
                  {upcomingGame.homeAbbr} vs {upcomingGame.awayAbbr}
                </span>
              </>
            )}
          </div>
        </div>
      )}
      
      <button
        onClick={onViewDetails}
        className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-all bg-secondary/80 text-muted-foreground hover:bg-primary/10 hover:text-primary group-hover:bg-primary/10 group-hover:text-primary"
      >
        {hasPremiumAccess ? (
          <>
            <BarChart3 className="w-3.5 h-3.5" />
            {translations.viewFullStats}
          </>
        ) : (
          <>
            <Crown className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-amber-400">{translations.premiumStats}</span>
          </>
        )}
      </button>
    </div>
  );
};

export function YourPlayersSection() {
  const { favorites, hasFavoritePlayers, togglePlayerAlerts, getPlayerLimit, isAtPlayerLimit } = useUserFavorites();
  const { data: games } = useAllTodaysGames();
  const { user, subscription } = useAuth();
  const { t } = useLanguage();
  const { data: playerStatsData, isLoading: isLoadingStats } = useFavoritePlayerStats(favorites.players);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<FavoritePlayer | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  const hasPremiumAccess = subscription.subscribed && (subscription.tier === "pro" || subscription.tier === "elite");
  const hasPaidAccess = subscription.subscribed && subscription.tier !== null;
  const playerLimit = getPlayerLimit(subscription);
  const atLimit = isAtPlayerLimit(playerLimit);
  const isLimitedTier = subscription.subscribed && !subscription.isTrial && subscription.tier !== "elite";

  const playerGamesMap = useMemo(() => {
    if (!games || !hasFavoritePlayers) return new Map<number, TransformedGame>();
    const map = new Map<number, TransformedGame>();
    favorites.players.forEach((player) => {
      const matchingGame = games.find(
        (game) => game.league === player.league && (game.homeAbbr === player.team || game.awayAbbr === player.team)
      );
      if (matchingGame) map.set(player.id, matchingGame);
    });
    return map;
  }, [games, favorites.players, hasFavoritePlayers]);

  const playerStatsMap = useMemo(() => {
    const map = new Map<string, LivePlayerStats | null>();
    if (playerStatsData) {
      playerStatsData.forEach((stats, key) => map.set(key, stats));
    }
    return map;
  }, [playerStatsData]);

  if (!user) {
    return (
      <>
        <section className="py-12 container mx-auto px-6">
          <div className="glass-card p-8 text-center border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <Lock className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-foreground mb-2">
              {t.yourPlayers.signInTitle}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {t.yourPlayers.signInDesc}
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t.yourPlayers.signIn}
            </button>
          </div>
        </section>
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </>
    );
  }

  if (!hasFavoritePlayers) {
    return (
      <>
        <section className="py-12 container mx-auto px-6">
          <div className="glass-card p-8 text-center border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <User className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-foreground mb-2">
              {t.yourPlayers.followTitle}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {t.yourPlayers.followDesc}
            </p>
            {hasPaidAccess ? (
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Star className="w-4 h-4" />
                {t.yourPlayers.choosePlayers}
              </button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-amber-500/10 to-amber-500/5 rounded-xl border border-amber-500/20 max-w-md mx-auto">
                  <div className="flex items-center gap-3 justify-center">
                    <Crown className="w-5 h-5 text-amber-400" />
                    <p className="text-sm font-medium text-foreground">{t.yourPlayers.upgradePro}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Crown className="w-4 h-4" />
                  {t.yourPlayers.upgradeNow}
                </button>
              </div>
            )}
          </div>
        </section>
        <FavoritePlayersModal isOpen={showModal} onClose={() => setShowModal(false)} />
        <SubscriptionModal 
          isOpen={showSubscriptionModal} 
          onClose={() => setShowSubscriptionModal(false)} 
          onNeedAuth={() => setShowAuthModal(true)}
        />
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </>
    );
  }

  return (
    <>
      <section className="py-12 container mx-auto px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold text-foreground">
                {t.yourPlayers.title}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm text-muted-foreground">
                  {favorites.players.length} / {playerLimit} players across {new Set(favorites.players.map(p => p.league)).size} league{new Set(favorites.players.map(p => p.league)).size !== 1 ? 's' : ''}
                </p>
                {isLimitedTier && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 flex items-center gap-1">
                    <Crown className="w-3 h-3" />
                    Elite: {PLAYER_LIMITS.elite} players
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasPaidAccess ? (
              <button
                onClick={() => togglePlayerAlerts(!favorites.playerAlertsEnabled)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  favorites.playerAlertsEnabled
                    ? "bg-primary/10 text-primary"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {favorites.playerAlertsEnabled ? (
                  <Bell className="w-4 h-4" />
                ) : (
                  <BellOff className="w-4 h-4" />
                )}
                {favorites.playerAlertsEnabled ? t.yourPlayers.alertsOn : t.yourPlayers.alertsOff}
              </button>
            ) : null}
            {hasPaidAccess ? (
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-secondary text-muted-foreground hover:text-foreground rounded-lg transition-colors"
              >
                <Settings2 className="w-4 h-4" />
                {t.yourPlayers.edit}
              </button>
            ) : (
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded-lg transition-colors"
              >
                <Crown className="w-4 h-4" />
                {t.yourPlayers.upgradeToEdit}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {favorites.players.slice(0, 8).map((player) => (
            <PlayerCard
              key={`${player.league}-${player.id}`}
              player={player}
              upcomingGame={playerGamesMap.get(player.id)}
              stats={playerStatsMap.get(`${player.league}-${player.id}`)}
              isLoading={isLoadingStats}
              hasPremiumAccess={hasPremiumAccess}
              translations={{ viewFullStats: t.yourPlayers.viewFullStats, premiumStats: t.yourPlayers.premiumStats, signInStats: t.yourPlayers.signInStats }}
              onViewDetails={() => {
                setSelectedPlayer(player);
                setShowPremiumModal(true);
              }}
            />
          ))}
        </div>

        {favorites.players.length > 8 && (
          <div className="flex justify-center mt-6">
            <button 
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {t.yourPlayers.viewAll} {favorites.players.length} players
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>
      <FavoritePlayersModal isOpen={showModal} onClose={() => setShowModal(false)} />
      <PremiumPlayerStatsModal
        isOpen={showPremiumModal}
        onClose={() => { setShowPremiumModal(false); setSelectedPlayer(null); }}
        player={selectedPlayer}
      />
      <SubscriptionModal 
        isOpen={showSubscriptionModal} 
        onClose={() => setShowSubscriptionModal(false)} 
        onNeedAuth={() => setShowAuthModal(true)}
      />
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}

export default YourPlayersSection;
